import { styled } from "@linaria/react";
import React from "react";
import { useIndexedProjects } from "../../../api/useProjects";
import { useRevalidate } from "../../../api/useQuery";
import type { User, ProjectRole, GlobalRole } from "../../../api/useUser";
import type { UserProject, UserProfil } from "../../../api/useUsers";
import { canUserDoOnProject } from "diagnostics-domain/canUserDo";
import useModal from "@ui/lib/hooks/useModal";
import useToasts from "@ui/Toast/useToasts";
import t from "../../../i18n";
import { color } from "@ui";
import Modal from "@ui/Modal";
import ModalForm from "@ui/Modal/Form";
import ProjectRoleMenu, { getProjectRoleName } from "./ProjectRoleMenu";
import type { ProjectRoleOption, UserProjectRoleOption } from "./ProjectRolesOptions";
import updateProjectRole from "./updateProjectRole";
import { FlaskFill } from "@ui/IconV2/react";

const ProjectRoleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 5px 0;
  width: 100%;
`;

const ProjectName = styled.div`
  display: flex;
  align-items: baseline;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const IconWrapper = styled.div`
  display: grid;
  place-items: center;
`;

const StyledLab = styled(FlaskFill)`
  height: 14px;
  width: 14px;
  margin-left: 4px;
  color: ${color.carbon800};
`;

type AllRole = GlobalRole | ProjectRole;
export const rolePrecedence: { [key in AllRole]: number } = {
  PROD_READER: 0,
  ALL_PROJECTS_READER_WITH_JUPYTER: 1,
  ALL_PROJECTS_READER: 1,
  PROJECT_READER: 1,
  DIAGNOSER: 1,
  PROJECT_MANAGER_READER: 1,
  PROJECT_WRITER: 2,
  PROJECT_MANAGER_WRITER: 2,
  ALL_PROJECTS_WRITER: 2,
  PROJECT_SETUPER: 3,
  PROJECT_MANAGER_SETUPER: 3,
  ALL_PROJECTS_SETUPER: 3,
  ADMIN: 4,
  PROJECT_MANAGER: 4,
  SUPER_ADMIN: 5,
};

interface ProjectRoleP {
  project: UserProject;
  projectName: string;
  isProdProject: boolean;
  userProfil: UserProfil;
  user: User;
}
function getNewProjectRole(
  roleClicked: UserProjectRoleOption,
  oldRole: ProjectRole,
): ProjectRole | null {
  const isAdmin = oldRole?.includes("MANAGER");
  const isReader = oldRole?.includes("READER");
  const isWritter = oldRole?.includes("WRITER");
  const isSetuper = oldRole?.includes("SETUPER");

  switch (roleClicked) {
    case "PROJECT_MANAGER":
      return isAdmin
        ? (isReader && "PROJECT_READER") ||
            (isWritter && "PROJECT_WRITER") ||
            (isSetuper && "PROJECT_SETUPER") ||
            null
        : (isReader && "PROJECT_MANAGER_READER") ||
            (isWritter && "PROJECT_MANAGER_WRITER") ||
            (isSetuper && "PROJECT_MANAGER_SETUPER") ||
            "PROJECT_MANAGER";
    case "PROJECT_READER":
      return isReader
        ? isAdmin
          ? "PROJECT_MANAGER"
          : null
        : isAdmin
          ? "PROJECT_MANAGER_READER"
          : "PROJECT_READER";

    case "PROJECT_WRITER":
      return isWritter
        ? isAdmin
          ? "PROJECT_MANAGER"
          : null
        : isAdmin
          ? "PROJECT_MANAGER_WRITER"
          : "PROJECT_WRITER";

    case "PROJECT_SETUPER":
      return isSetuper
        ? isAdmin
          ? "PROJECT_MANAGER"
          : null
        : isAdmin
          ? "PROJECT_MANAGER_SETUPER"
          : "PROJECT_SETUPER";

    default:
      throw new Error(roleClicked satisfies never);
  }
}

const ProjectRole = ({ project, projectName, isProdProject, user, userProfil }: ProjectRoleP) => {
  const { modal, closeModal, openModal } = useModal();
  const { addToast } = useToasts();
  const canChangeProjectRole = canUserDoOnProject(user, "ASSIGN_PROJECT_ROLE", project.projectId);
  const revalidateUsers = useRevalidate();
  const isSelf = userProfil.id === user.id;

  const openDeleteModal = React.useCallback(() => {
    openModal();
  }, [openModal]);

  const updateRoles = React.useCallback(
    async (projectId: string, newRole: ProjectRole, clickedRole: UserProjectRoleOption) => {
      try {
        const messageVariables = {
          userName: userProfil.login,
          projectName,
        };
        await updateProjectRole(userProfil.id, projectId, newRole);
        await revalidateUsers("/users");
        const message = project.role.includes(clickedRole)
          ? t("users.table.row.roles.project.toast_remove_role", {
              ...messageVariables,
              role: getProjectRoleName(clickedRole),
            })
          : t("users.table.row.roles.project.toast_add_role", {
              ...messageVariables,
              role: getProjectRoleName(newRole),
            });
        addToast("success", message);
      } catch {
        addToast("error", t("errors.backend_error"));
      }

      return true;
    },
    [addToast, projectName, revalidateUsers, userProfil.id, project.role, userProfil.login],
  );

  const handleRoleClick = React.useCallback(
    async (clickedRole: UserProjectRoleOption) => {
      if (isSelf) return;
      const newProjectRole = getNewProjectRole(clickedRole, project.role);

      if (newProjectRole === null) {
        openDeleteModal();
        return;
      }

      await updateRoles(project.projectId, newProjectRole, clickedRole);
    },
    [isSelf, project, updateRoles, openDeleteModal],
  );

  const disableOption = React.useCallback(
    (roleName: AllRole) =>
      userProfil.roles.global !== null &&
      rolePrecedence[userProfil.roles.global] >= rolePrecedence[roleName],
    [userProfil],
  );

  const handleRoleDelete = React.useCallback(async () => {
    if (isSelf) return false;
    try {
      const messageVariables = {
        userName: userProfil.login,
        projectName,
      };
      await updateProjectRole(userProfil.id, project.projectId, null);
      await revalidateUsers("/users");
      const message = t("users.table.row.roles.project.toast_remove_project", {
        ...messageVariables,
      });
      addToast("success", message);
    } catch {
      addToast("error", t("errors.backend_error"));
    }

    return true;
  }, [
    isSelf,
    project.projectId,
    addToast,
    projectName,
    revalidateUsers,
    userProfil.id,
    userProfil.login,
  ]);

  const options: ProjectRoleOption[] = React.useMemo(
    () => [
      {
        titleLabel: t("users.table.row.menu.roles.project.read.title"),
        descriptionLabel: t("users.table.row.menu.roles.project.read.description"),
        value: "PROJECT_READER",
        disabled: isSelf || !canChangeProjectRole || disableOption("PROJECT_READER"),
      },
      {
        titleLabel: t("users.table.row.menu.roles.project.write.title"),
        descriptionLabel: t("users.table.row.menu.roles.project.write.description"),
        value: "PROJECT_WRITER",
        disabled: isSelf || !canChangeProjectRole || disableOption("PROJECT_WRITER"),
      },
      {
        titleLabel: t("users.table.row.menu.roles.project.setup.title"),
        descriptionLabel: t("users.table.row.menu.roles.project.setup.description"),
        value: "PROJECT_SETUPER",
        disabled: isSelf || !canChangeProjectRole || disableOption("PROJECT_SETUPER"),
      },
      {
        titleLabel: t("users.table.row.menu.roles.project.admin.title"),
        descriptionLabel: t("users.table.row.menu.roles.project.admin.description"),
        value: "PROJECT_MANAGER",
        disabled: isSelf || !canChangeProjectRole || disableOption("PROJECT_MANAGER"),
      },
    ],
    [disableOption, canChangeProjectRole, isSelf],
  );

  return (
    <>
      <Modal
        title={t("users.table.row.modal.title.delete_specific_role")}
        isOpen={modal}
        onRequestClose={closeModal}
      >
        <ModalForm
          onRequestClose={closeModal}
          onSubmit={handleRoleDelete}
          okButtonVariant="destructive"
          okButtonName={t("modal.delete")}
        >
          {t("users.table.row.modal.content.delete_specific_role", {
            name: projectName,
          })}
        </ModalForm>
      </Modal>
      <ProjectRoleRow>
        <ProjectName title={projectName}>
          {projectName}
          {isProdProject ? null : (
            <IconWrapper title={t("commons.tooltip.research")}>
              <StyledLab />
            </IconWrapper>
          )}
        </ProjectName>
        <ProjectRoleMenu project={project} options={options} onRoleClick={handleRoleClick} />
      </ProjectRoleRow>
    </>
  );
};
const ProjectRolesWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  width: 100%;
  // & > :last-child {
  //   margin-bottom: 10px;
  // }
`;

interface P {
  userProjects: UserProject[];
  userProfil: UserProfil;
  user: User;
}

const ProjectRoles = ({ userProjects, userProfil, user }: P) => {
  const projects = useIndexedProjects();

  const sortedList = React.useMemo(
    () =>
      userProjects
        .map(project => {
          const projectMatch = projects[project.projectId];
          if (!projectMatch) throw new Error(`project ${project.projectId} not found`);
          return {
            projectId: project.projectId,
            role: project.role,
            name: projectMatch.name,
            draft: projectMatch.draft,
          };
        })
        .sort((a, b) => a.name.localeCompare(b.name)),
    [userProjects, projects],
  );

  return (
    <ProjectRolesWrapper>
      {sortedList.map(project => (
        <ProjectRole
          key={project.projectId}
          user={user}
          project={project}
          projectName={project.name}
          isProdProject={!project.draft}
          userProfil={userProfil}
        />
      ))}
    </ProjectRolesWrapper>
  );
};
export default React.memo(ProjectRoles);
